// OCPP versions the charge point can speak, in the order it offers them. OCPP 1.6J is the
// default; the same lists are maintained in ocpp-server (src/ocpp/catalog.ts).
export const OCPP_PROTOCOLS = ["ocpp1.6", "ocpp2.0.1", "ocpp2.1"] as const;
export type OcppProtocol = (typeof OCPP_PROTOCOLS)[number];
export const DEFAULT_OCPP_PROTOCOL: OcppProtocol = "ocpp1.6";

export const isOcppProtocol = (value: unknown): value is OcppProtocol =>
  typeof value === "string" && (OCPP_PROTOCOLS as readonly string[]).includes(value);

export const isOcpp2 = (protocol: OcppProtocol): boolean => protocol !== "ocpp1.6";

export const PROTOCOL_LABELS: Record<OcppProtocol, string> = {
  "ocpp1.6": "OCPP 1.6J",
  "ocpp2.0.1": "OCPP 2.0.1",
  "ocpp2.1": "OCPP 2.1",
};

/** Messages the charge point initiates (OCPP 1.6 incl. Security extension, 2.0.1, 2.1). */
export const CP_INITIATED: Record<OcppProtocol, readonly string[]> = {
  "ocpp1.6": [
    "Authorize",
    "BootNotification",
    "DataTransfer",
    "DiagnosticsStatusNotification",
    "FirmwareStatusNotification",
    "Heartbeat",
    "MeterValues",
    "StartTransaction",
    "StatusNotification",
    "StopTransaction",
    "LogStatusNotification",
    "SecurityEventNotification",
    "SignCertificate",
    "SignedFirmwareStatusNotification",
  ],
  "ocpp2.0.1": [
    "Authorize",
    "BootNotification",
    "ClearedChargingLimit",
    "DataTransfer",
    "FirmwareStatusNotification",
    "Get15118EVCertificate",
    "GetCertificateStatus",
    "Heartbeat",
    "LogStatusNotification",
    "MeterValues",
    "NotifyChargingLimit",
    "NotifyCustomerInformation",
    "NotifyDisplayMessages",
    "NotifyEVChargingNeeds",
    "NotifyEVChargingSchedule",
    "NotifyEvent",
    "NotifyMonitoringReport",
    "NotifyReport",
    "PublishFirmwareStatusNotification",
    "ReportChargingProfiles",
    "ReservationStatusUpdate",
    "SecurityEventNotification",
    "SignCertificate",
    "StatusNotification",
    "TransactionEvent",
  ],
  "ocpp2.1": [
    "Authorize",
    "BatterySwap",
    "BootNotification",
    "ClearedChargingLimit",
    "ClosePeriodicEventStream",
    "DataTransfer",
    "FirmwareStatusNotification",
    "Get15118EVCertificate",
    "GetCertificateChainStatus",
    "GetCertificateStatus",
    "Heartbeat",
    "LogStatusNotification",
    "MeterValues",
    "NotifyChargingLimit",
    "NotifyCustomerInformation",
    "NotifyDERAlarm",
    "NotifyDERStartStop",
    "NotifyDisplayMessages",
    "NotifyEVChargingNeeds",
    "NotifyEVChargingSchedule",
    "NotifyEvent",
    "NotifyMonitoringReport",
    "NotifyPeriodicEventStream",
    "NotifyPriorityCharging",
    "NotifyReport",
    "NotifySettlement",
    "OpenPeriodicEventStream",
    "PublishFirmwareStatusNotification",
    "PullDynamicScheduleUpdate",
    "ReportChargingProfiles",
    "ReportDERControl",
    "ReservationStatusUpdate",
    "SecurityEventNotification",
    "SignCertificate",
    "StatusNotification",
    "TransactionEvent",
    "VatNumberValidation",
  ],
};

/** OCPP 2.1 SEND messages (message type 6): never answered. */
export const SEND_MESSAGES: Record<OcppProtocol, readonly string[]> = {
  "ocpp1.6": [],
  "ocpp2.0.1": [],
  "ocpp2.1": ["NotifyPeriodicEventStream"],
};
